import React from "react";

import { Link, useParams } from "react-router-dom";

import {
  ArrowLeft,
  ArrowRight,
  CalendarDays,
  Clock3,
  Eye,
  Share2,
  Leaf,
} from "lucide-react";

import Blog1 from "../image/blog-1.png";
import Blog2 from "../image/blog-2.png";
import Blog3 from "../image/blog-3.png";
import Blog4 from "../image/blog-4.png";
import Blog5 from "../image/blog-5.png";
import Blog6 from "../image/blog-6.png";
import Blog7 from "../image/blog-7.png";
import Blog8 from "../image/blog-8.png";




// =====================================================
// BLOG DATA
// =====================================================

const blogData = {
  "hydroponic-farming-guide": {
    image: Blog1,
    category: "Hydroponics Basics",
    title: "How Hydroponic Farming Works: A Complete Guide for Beginners",
    date: "Sep 20, 2026",
    readTime: "8 min read",
    views: "2.4K views",

    intro:
      "Hydroponic farming is a modern method of growing plants without traditional soil. Instead of depending on soil for nutrients, plants receive a carefully balanced nutrient solution directly through their roots.",

    sections: [
      {
        title: "What is Hydroponic Farming?",
        content:
          "Hydroponics is a soilless farming technique where plants grow in a controlled environment using water, nutrients, oxygen and light. The roots receive everything they need to grow without relying on conventional agricultural soil.",
      },
      {
        title: "How Does Hydroponics Work?",
        content:
          "In a hydroponic system, plant roots are exposed to nutrient-rich water. Depending on the system, roots may remain directly in water or receive nutrients through flowing channels, suspended containers or mist.",
      },
      {
        title: "Benefits of Hydroponic Farming",
        content:
          "Hydroponic farming can make efficient use of water and growing space while allowing farmers to carefully manage nutrients and environmental conditions. This makes it suitable for urban farming, commercial production and controlled agricultural environments.",
      },
    ],

    benefits: [
      "Uses significantly less water than traditional farming",
      "Requires less growing space",
      "No traditional agricultural soil is required",
      "Better control over nutrients",
      "Suitable for controlled environments",
      "Can support year-round crop production",
    ],
  },

  "best-crops-hydroponic-farming": {
    image: Blog1,
    category: "Growing Guides",
    title: "Best Crops for Hydroponic Farming in India",
    date: "Sep 18, 2026",
    readTime: "6 min read",
    views: "1.8K views",

    intro:
      "Hydroponic farming allows growers to cultivate a wide variety of crops in controlled environments. Choosing the right crop is one of the most important decisions when starting a hydroponic farm.",

    sections: [
      {
        title: "Leafy Greens",
        content:
          "Lettuce, spinach, kale and other leafy greens are commonly suited to hydroponic systems because they have relatively short growing cycles and can perform well in nutrient-rich water.",
      },
      {
        title: "Herbs",
        content:
          "Basil, mint, coriander and other herbs can be grown efficiently in hydroponic systems. These crops can be particularly useful for smaller commercial and urban farming setups.",
      },
      {
        title: "Fruiting Crops",
        content:
          "Tomatoes, cucumbers and strawberries can also be cultivated hydroponically when appropriate support, lighting, pollination and environmental controls are provided.",
      },
    ],

    benefits: [
      "Lettuce",
      "Spinach",
      "Basil",
      "Coriander",
      "Mint",
      "Tomatoes",
      "Cucumber",
      "Strawberries",
    ],
  },

  "water-conservation-hydroponics": {
    image: Blog3,
    category: "Sustainability",
    title: "Water Conservation Through Hydroponic Farming",
    date: "Sep 12, 2026",
    readTime: "5 min read",
    views: "1.2K views",

    intro:
      "Water efficiency is one of the major reasons hydroponic farming has attracted attention as a modern agricultural technology.",

    sections: [
      {
        title: "Why Water Efficiency Matters",
        content:
          "Traditional agriculture can lose significant amounts of water through evaporation, runoff and inefficient irrigation. Controlled hydroponic systems can recirculate nutrient water and reduce unnecessary losses.",
      },
      {
        title: "Recirculating Water Systems",
        content:
          "Many hydroponic systems continuously circulate nutrient-rich water through growing channels and return unused solution to a reservoir for monitoring and reuse.",
      },
      {
        title: "A More Controlled Growing Environment",
        content:
          "Because water delivery can be monitored and controlled, growers can provide plants with water according to their actual growing requirements.",
      },
    ],

    benefits: [
      "Efficient water circulation",
      "Reduced water wastage",
      "Controlled irrigation",
      "Nutrient monitoring",
      "Better resource management",
    ],
  },

  "growing-strawberries-hydroponics": {
    image: Blog4,
    category: "Crop Management",
    title: "Growing Strawberries in Hydroponics",
    date: "Sep 10, 2026",
    readTime: "8 min read",
    views: "980 views",

    intro:
      "Strawberries are an attractive crop for controlled-environment farming because hydroponic systems can provide growers with greater control over water, nutrients and growing conditions.",

    sections: [
      {
        title: "Choosing the Right System",
        content:
          "Strawberries can be grown using several hydroponic configurations. The system should provide good root aeration, drainage and easy access for crop maintenance.",
      },
      {
        title: "Managing Nutrients",
        content:
          "Nutrient concentration and pH should be monitored regularly. A stable nutrient solution helps plants maintain healthy growth and fruit production.",
      },
      {
        title: "Environmental Conditions",
        content:
          "Temperature, humidity, airflow and light all play important roles in strawberry production. Controlled environments allow growers to manage these factors more consistently.",
      },
    ],

    benefits: [
      "Efficient use of growing space",
      "Cleaner growing environment",
      "Better environmental control",
      "Efficient irrigation",
      "Suitable for controlled production",
    ],
  },

  "future-vertical-farming-india": {
    image: Blog5,
    category: "Industry Insights",
    title: "The Future of Vertical Farming in India",
    date: "Sep 05, 2026",
    readTime: "6 min read",
    views: "1.1K views",

    intro:
      "Vertical farming combines controlled environments, efficient space utilization and modern agricultural technology to grow crops in stacked systems.",

    sections: [
      {
        title: "What is Vertical Farming?",
        content:
          "Vertical farming grows crops in vertically arranged layers instead of spreading cultivation across large horizontal areas. It can be implemented indoors, inside greenhouses or in specially designed farming facilities.",
      },
      {
        title: "Why Vertical Farming Matters",
        content:
          "Growing vertically can increase production capacity per unit of floor space. This can be useful in locations where land availability is limited.",
      },
      {
        title: "Technology and Automation",
        content:
          "Sensors, automation, environmental controls and data analytics can help growers monitor and manage modern vertical farming systems.",
      },
    ],

    benefits: [
      "Efficient land utilization",
      "Controlled growing conditions",
      "Automation opportunities",
      "Local food production",
      "Potential for urban farming",
    ],
  },

  "nft-vs-dwc-vs-aeroponics": {
    image: Blog6,
    category: "Hydroponics Basics",
    title: "NFT vs DWC vs Aeroponics: Which is Better?",
    date: "Sep 01, 2026",
    readTime: "7 min read",
    views: "920 views",

    intro:
      "Different hydroponic systems use different methods to deliver water, nutrients and oxygen to plant roots. NFT, DWC and aeroponics are three popular approaches.",

    sections: [
      {
        title: "NFT System",
        content:
          "Nutrient Film Technique systems circulate a thin film of nutrient solution through growing channels. Plant roots receive nutrients while remaining exposed to oxygen.",
      },
      {
        title: "DWC System",
        content:
          "Deep Water Culture keeps plant roots suspended in nutrient-rich water. Air pumps are commonly used to provide oxygen to the root zone.",
      },
      {
        title: "Aeroponics",
        content:
          "Aeroponic systems suspend plant roots in an air environment and deliver nutrient solution through a fine mist.",
      },
    ],

    benefits: [
      "NFT – efficient and scalable",
      "DWC – simple and accessible",
      "Aeroponics – high root oxygen availability",
      "System selection depends on crop and farm requirements",
    ],
  },

  "iot-modern-farming": {
    image: Blog7,
    category: "Smart Technology",
    title: "How IoT is Transforming Modern Farming",
    date: "Aug 28, 2026",
    readTime: "6 min read",
    views: "850 views",

    intro:
      "Internet of Things technology is helping modern farms collect real-time information about crops, water, nutrients and environmental conditions.",

    sections: [
      {
        title: "Connected Sensors",
        content:
          "IoT sensors can monitor parameters such as temperature, humidity, pH, electrical conductivity and water conditions.",
      },
      {
        title: "Real-Time Monitoring",
        content:
          "Connected dashboards allow growers to view important farm conditions from a central interface and identify changes more quickly.",
      },
      {
        title: "Automation",
        content:
          "IoT systems can be connected to pumps, nutrient dosing equipment, climate systems and other devices to support automated farm operations.",
      },
    ],

    benefits: [
      "Real-time farm monitoring",
      "Sensor-based decision making",
      "Automated irrigation",
      "Environmental monitoring",
      "Data-driven farming",
    ],
  },

  "sustainable-food-system": {
    image: Blog8,
    category: "Sustainability",
    title: "Building a Sustainable Food System",
    date: "Aug 25, 2026",
    readTime: "5 min read",
    views: "790 views",

    intro:
      "A sustainable food system requires efficient use of resources while maintaining reliable production of fresh and nutritious food.",

    sections: [
      {
        title: "Resource Efficiency",
        content:
          "Modern farming technologies can help optimize water, nutrients, energy and growing space through controlled production systems.",
      },
      {
        title: "Technology and Agriculture",
        content:
          "Sensors, automation and data analytics can help growers understand crop requirements and improve farm management.",
      },
      {
        title: "Local Food Production",
        content:
          "Controlled-environment agriculture can support food production closer to consumers, particularly where traditional agricultural land is limited.",
      },
    ],

    benefits: [
      "Efficient resource utilization",
      "Controlled crop production",
      "Technology-enabled farming",
      "Reduced agricultural waste",
      "Local food production",
    ],
  },
};


// =====================================================
// RELATED BLOGS
// =====================================================

const relatedBlogs = [
  {
    image: Blog1,
    title: "Best Crops for Hydroponic Farming in India",
    category: "Growing Guides",
    slug: "best-crops-hydroponic-farming",
  },
  {
    image: Blog3,
    title: "Water Conservation Through Hydroponic Farming",
    category: "Sustainability",
    slug: "water-conservation-hydroponics",
  },
  {
    image: Blog7,
    title: "How IoT is Transforming Modern Farming",
    category: "Smart Technology",
    slug: "iot-modern-farming",
  },
];


// =====================================================
// BLOG DETAILS
// =====================================================

export default function BlogDetails() {
  const { slug } = useParams();

  const blog = blogData[slug] || blogData["hydroponic-farming-guide"];

  return (
    <main className="min-h-screen bg-white text-[#10231c]">

      {/* =================================================
          BREADCRUMB
      ================================================= */}

      <section className="border-b border-green-100 bg-[#f7fbf7]">

        <div className="mx-auto max-w-[1200px] px-5 py-4 lg:px-10">

          <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">

            <Link
              to="/"
              className="hover:text-[#087f4f]"
            >
              Home
            </Link>

            <span>›</span>

            <Link
              to="/blog"
              className="hover:text-[#087f4f]"
            >
              Blog
            </Link>

            <span>›</span>

            <span className="text-gray-700">
              {blog.category}
            </span>

          </div>

        </div>

      </section>


      {/* =================================================
          ARTICLE HEADER
      ================================================= */}

      <section className="bg-[#f7fbf7] pb-10 pt-8 lg:pb-14 lg:pt-12">

        <div className="mx-auto max-w-[1000px] px-5 text-center lg:px-10">

          {/* Category */}
          <span className="inline-flex rounded-full bg-green-100 px-4 py-1.5 text-xs font-bold text-[#087f4f]">
            {blog.category}
          </span>


          {/* Title */}
          <h1 className="mx-auto mt-5 max-w-[850px] text-3xl font-extrabold leading-tight sm:text-4xl lg:text-5xl">
            {blog.title}
          </h1>


          {/* Meta */}
          <div className="mt-5 flex flex-wrap items-center justify-center gap-4 text-xs text-gray-500">

            <span className="flex items-center gap-1.5">
              <CalendarDays size={15} />
              {blog.date}
            </span>

            <span className="flex items-center gap-1.5">
              <Clock3 size={15} />
              {blog.readTime}
            </span>

            <span className="flex items-center gap-1.5">
              <Eye size={15} />
              {blog.views}
            </span>

          </div>

        </div>

      </section>


      {/* =================================================
          HERO IMAGE
      ================================================= */}

      <section className="-mt-1">

        <div className="mx-auto max-w-[1200px] px-5 lg:px-10">

          <div className="overflow-hidden rounded-2xl shadow-lg">

            <img
              src={blog.image}
              alt={blog.title}
              className="h-[300px] w-full object-cover sm:h-[400px] lg:h-[520px]"
            />

          </div>

        </div>

      </section>


      {/* =================================================
          ARTICLE CONTENT
      ================================================= */}

      <section className="py-12 lg:py-16">

        <div className="mx-auto grid max-w-[1200px] gap-10 px-5 lg:grid-cols-[1fr_280px] lg:px-10">

          {/* =================================================
              CONTENT
          ================================================= */}

          <article>

            {/* Intro */}
            <p className="text-lg font-medium leading-8 text-gray-700">
              {blog.intro}
            </p>


            {/* Green line */}
            <div className="my-8 h-1 w-16 rounded-full bg-[#087f4f]" />


            {/* Sections */}
            {blog.sections.map((section, index) => (
              <div
                key={index}
                className="mb-9"
              >

                <h2 className="text-2xl font-extrabold text-[#10231c] sm:text-3xl">
                  {section.title}
                </h2>

                <p className="mt-4 text-base leading-8 text-gray-600">
                  {section.content}
                </p>

              </div>
            ))}


            {/* =================================================
                KEY BENEFITS
            ================================================= */}

            <div className="rounded-2xl bg-[#f1faf5] p-6 lg:p-8">

              <div className="flex items-center gap-3">

                <div className="flex h-11 w-11 items-center justify-center rounded-full bg-green-100 text-[#087f4f]">
                  <Leaf size={22} />
                </div>

                <h2 className="text-xl font-extrabold">
                  Key Takeaways
                </h2>

              </div>


              <div className="mt-5 grid gap-3 sm:grid-cols-2">

                {blog.benefits.map((benefit, index) => (

                  <div
                    key={index}
                    className="flex gap-3 text-sm leading-6 text-gray-600"
                  >

                    <span className="mt-1 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-[#087f4f] text-xs text-white">
                      ✓
                    </span>

                    <span>
                      {benefit}
                    </span>

                  </div>

                ))}

              </div>

            </div>


            {/* =================================================
                CONCLUSION
            ================================================= */}

            <div className="mt-10">

              <h2 className="text-2xl font-extrabold">
                Conclusion
              </h2>

              <p className="mt-4 text-base leading-8 text-gray-600">
                Modern agriculture is evolving through technology,
                better resource management and innovative growing
                systems. Hydroponics and smart farming technologies
                can help create controlled and efficient production
                environments for the future.
              </p>

              <p className="mt-4 text-base leading-8 text-gray-600">
                At Polybionic Technologies, we believe that technology
                and nature can work together to build a more sustainable
                and food-secure future.
              </p>

            </div>


            {/* =================================================
                SHARE
            ================================================= */}

            <div className="mt-10 flex flex-wrap items-center justify-between gap-4 border-y border-gray-200 py-5">

              <div className="flex items-center gap-2">

                <Share2
                  size={18}
                  className="text-[#087f4f]"
                />

                <span className="text-sm font-bold">
                  Share this article
                </span>

              </div>


              <div className="flex gap-2">

                {/* <ShareButton icon={<Facebook size={15} />} />
                <ShareButton icon={<Twitter size={15} />} />
                <ShareButton icon={<Linkedin size={15} />} /> */}

              </div>

            </div>


            {/* Back */}
            <Link
              to="/blog"
              className="mt-7 inline-flex items-center gap-2 text-sm font-bold text-[#087f4f]"
            >
              <ArrowLeft size={16} />
              Back to Blog
            </Link>

          </article>


          {/* =================================================
              SIDEBAR
          ================================================= */}

          <aside>

            <div className="sticky top-24">

              {/* Table of contents */}
              <div className="rounded-2xl border border-gray-100 bg-[#f7fbf7] p-5">

                <div className="flex items-center gap-2">

                  {/* <BookOpen
                    size={19}
                    className="text-[#087f4f]"
                  /> */}

                  <h3 className="font-extrabold">
                    In This Article
                  </h3>

                </div>


                <div className="mt-4 space-y-2">

                  {blog.sections.map((section, index) => (

                    <a
                      href={`#section-${index}`}
                      key={index}
                      className="block border-l-2 border-green-100 py-1 pl-3 text-sm text-gray-600 transition hover:border-[#087f4f] hover:text-[#087f4f]"
                    >
                      {section.title}
                    </a>

                  ))}

                  <a
                    href="#conclusion"
                    className="block border-l-2 border-green-100 py-1 pl-3 text-sm text-gray-600 hover:border-[#087f4f] hover:text-[#087f4f]"
                  >
                    Conclusion
                  </a>

                </div>

              </div>


              {/* CTA */}
              <div className="mt-5 overflow-hidden rounded-2xl bg-[#087f4f] p-6 text-white">

                <Leaf
                  size={30}
                  className="mb-4"
                />

                <h3 className="text-xl font-extrabold">
                  Build a Greener Future
                </h3>

                <p className="mt-2 text-sm leading-6 text-white/80">
                  Discover innovative hydroponic farming solutions
                  for your business or community.
                </p>

                <Link
                  to="/contact"
                  className="mt-5 inline-flex items-center gap-2 rounded-full bg-white px-5 py-2.5 text-xs font-bold text-[#087f4f]"
                >
                  Talk to Our Experts
                  <ArrowRight size={14} />
                </Link>

              </div>

            </div>

          </aside>

        </div>

      </section>


      {/* =================================================
          RELATED ARTICLES
      ================================================= */}

      <section className="border-t border-green-100 bg-[#f7fbf7] py-12 lg:py-16">

        <div className="mx-auto max-w-[1200px] px-5 lg:px-10">

          <div className="flex items-end justify-between">

            <div>

              <p className="text-xs font-bold uppercase tracking-[0.15em] text-[#16854f]">
                Keep Learning
              </p>

              <h2 className="mt-2 text-2xl font-extrabold sm:text-3xl">
                Related Articles
              </h2>

            </div>


            <Link
              to="/blog"
              className="hidden items-center gap-2 text-sm font-bold text-[#087f4f] sm:flex"
            >
              View All
              <ArrowRight size={15} />
            </Link>

          </div>


          <div className="mt-6 grid gap-5 md:grid-cols-3">

            {relatedBlogs.map((item, index) => (

              <Link
                key={index}
                to={`/blog/${item.slug}`}
                className="group overflow-hidden rounded-xl border border-gray-200 bg-white"
              >

                <div className="h-[180px] overflow-hidden">

                  <img
                    src={item.image}
                    alt={item.title}
                    className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                  />

                </div>


                <div className="p-5">

                  <span className="rounded-md bg-green-50 px-2 py-1 text-[10px] font-bold text-[#087f4f]">
                    {item.category}
                  </span>

                  <h3 className="mt-3 text-lg font-extrabold leading-6">
                    {item.title}
                  </h3>

                  <span className="mt-4 inline-flex items-center gap-2 text-xs font-bold text-[#087f4f]">
                    Read More
                    <ArrowRight size={13} />
                  </span>

                </div>

              </Link>

            ))}

          </div>

        </div>

      </section>

    </main>
  );
}


// =====================================================
// SHARE BUTTON
// =====================================================

function ShareButton({ icon }) {
  return (
    <button className="flex h-9 w-9 items-center justify-center rounded-full border border-gray-200 text-gray-600 transition hover:border-[#087f4f] hover:bg-[#087f4f] hover:text-white">
      {icon}
    </button>
  );
}